import type {
  GradingKnowledgeBaseAdherenceStatement,
  KnowledgeBaseAdherenceSource,
} from "@repo/app-types/entities/knowledgeBaseAdherence";
import type {
  AccountGradingRecording,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { QuoteParagraph } from "@repo/app-types/helpers/gradeValidationHelpers";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { MarkdownContent } from "@repo/ui/markdown/markdownContent";
import {
  SlideoverDialog,
  SlideoverDialogContentGray,
  SlideoverDialogHeader,
  SlideoverDialogTitle,
} from "@repo/ui/Slideover/slideoverDialog";
import { IconExternalLink, IconShare2 } from "@tabler/icons-react";
import { type FC, useCallback, useMemo } from "react";
import { Heading } from "react-aria-components";
import { Link } from "react-router";
import { appPaths } from "../../app/appPaths.js";
import { AudioPlayerProvider } from "../../components/audioPlayer/provider.js";
import { TranscriptViewerPane } from "../transcriptViewer/transcriptViewerPane.js";
import { useMatchQuoteToTranscript } from "../transcriptViewer/useMatchQuoteToTranscript.js";
import {
  ClickableQuote,
  type TranscriptFragmentData,
} from "./clickableQuote.js";
import { QuoteMatchMap } from "./gradeValidationHelpers.js";
import { KnowledgeBaseAdherenceCorrectnessIcon } from "./knowledgeBaseAdherenceCorrectnessIcon.js";
import { KnowledgeBaseDocumentTitle } from "./knowledgeBaseDocumentTitle.js";

export interface KnowledgeBaseAdherenceStatementSlideoverDialogProps {
  gradingId: GradingId;
  statementIndex: number;
  recordings: readonly AccountGradingRecording[];
  statement: GradingKnowledgeBaseAdherenceStatement;
  onSharePress?: () => void;
}

export const KnowledgeBaseAdherenceStatementSlideoverDialog: FC<
  KnowledgeBaseAdherenceStatementSlideoverDialogProps
> = (props) => {
  const { gradingId, statementIndex, onSharePress } = props;
  const { statement, recordings } = props;

  // Convert statement quotes to QuoteParagraph format for the QuoteMatcher
  const quoteParagraphs = useMemo((): readonly QuoteParagraph[] => {
    return statement.quotes.map((quote, index) => ({
      type: "quote" as const,
      id: `kb-quote-${index}`,
      short: quote.text,
      long: quote.text,
    }));
  }, [statement.quotes]);

  const {
    sortedRecordings,
    quoteMatches,
    handleQuotePress,
    transcriptRef,
    transcriptContainerRef,
  } = useMatchQuoteToTranscript({ recordings, quoteParagraphs });

  // Find fragment data for a given quote index (for displaying speaker/time in quote)
  const findFragmentDataForQuote = useCallback(
    (quoteIndex: number): TranscriptFragmentData | undefined => {
      const quoteId = `kb-quote-${quoteIndex}`;
      const matches = QuoteMatchMap.findByQuote(quoteMatches, quoteId);
      const firstMatch = matches?.[0];
      if (!firstMatch) {
        return undefined;
      }

      const recording = sortedRecordings.find(
        (r) => r.id === firstMatch.recordingId,
      );
      const transcript = recording?.transcript;
      if (!transcript) {
        return undefined;
      }

      const { recordingFragmentIndex } = firstMatch;
      const fragment = transcript.fragments[recordingFragmentIndex];
      if (!fragment) {
        return undefined;
      }

      return { recording, fragment };
    },
    [quoteMatches, sortedRecordings],
  );

  const firstSource = statement.sources[0];

  return (
    <SlideoverDialog label="Knowledge base adherence finding">
      <SlideoverDialogHeader
        isLoading={false}
        right={
          onSharePress ? (
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onSharePress}
              size={Size.MEDIUM}
              icon={
                <IconShare2
                  className="text-white-900 h-4 w-4"
                  aria-hidden="true"
                />
              }
            >
              Share
            </Button>
          ) : undefined
        }
      >
        <SlideoverDialogTitle>
          Knowledge base adherence finding
        </SlideoverDialogTitle>
      </SlideoverDialogHeader>

      <SlideoverDialogContentGray>
        <AudioPlayerProvider>
          <div className="grid h-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto]">
            {/* Left pane - Statement details */}
            <div className="relative space-y-6 overflow-y-auto border-r border-border-light p-4">
              {/* Statement summary + call quotes */}
              <div className="space-y-3 rounded-md border border-border-light bg-white p-4">
                <div className="flex items-start gap-2">
                  <div className="flex h-5 items-center">
                    <KnowledgeBaseAdherenceCorrectnessIcon
                      correctness={statement.correctness}
                    />
                  </div>
                  <div className="text-sm">{statement.summary}</div>
                </div>

                <div className="flex flex-col gap-3">
                  {statement.quotes.map((quote, index) => {
                    const quoteId = `kb-quote-${index}`;
                    const fragmentData = findFragmentDataForQuote(index);

                    if (!fragmentData) {
                      return null;
                    }

                    return (
                      <ClickableQuote
                        key={index}
                        fragmentData={fragmentData}
                        onPress={() => handleQuotePress(quoteId)}
                      >
                        {quote.text}
                      </ClickableQuote>
                    );
                  })}
                </div>
              </div>

              {/* Knowledge sources */}
              <section className="space-y-3 rounded-md border border-border-light bg-white p-4">
                <Heading
                  level={3}
                  className="text-sm font-semibold text-foreground"
                >
                  Knowledge sources
                </Heading>

                <ul className="list-disc space-y-3 pl-6">
                  {groupSourcesByArticle(statement.sources).map(
                    (group, groupIndex) => (
                      <li key={groupIndex}>
                        <div className="block text-sm font-medium">
                          <KnowledgeBaseDocumentTitle
                            knowledgeBaseId={group.knowledgeBaseId}
                            attachmentId={group.attachmentId}
                          />
                        </div>

                        {group.quotes.map((quote, quoteIndex) => (
                          <blockquote
                            key={quoteIndex}
                            className="my-0.5 prose prose-sm prose-tighter"
                          >
                            <MarkdownContent>{quote}</MarkdownContent>
                          </blockquote>
                        ))}
                      </li>
                    ),
                  )}
                </ul>

                {firstSource ? (
                  <p className="pt-2 text-sm text-muted-foreground">
                    Think this is wrong?{" "}
                    <Link
                      to={buildEditArticleUrl(
                        firstSource.knowledge_base_id,
                        firstSource.attachment_id,
                        gradingId,
                        statementIndex,
                      )}
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    >
                      Edit the source article
                      <IconExternalLink className="size-4" />
                    </Link>
                  </p>
                ) : null}
              </section>
            </div>

            {/* Right pane - Transcript + Audio */}
            <TranscriptViewerPane
              gradingId={gradingId}
              sortedRecordings={sortedRecordings}
              quoteMatches={quoteMatches}
              transcriptRef={transcriptRef}
              transcriptContainerRef={transcriptContainerRef}
            />
          </div>
        </AudioPlayerProvider>
      </SlideoverDialogContentGray>
    </SlideoverDialog>
  );
};

interface SourceGroup {
  knowledgeBaseId: string;
  attachmentId: string;
  quotes: string[];
}

function buildEditArticleUrl(
  knowledgeBaseId: string,
  attachmentId: string,
  gradingId: GradingId,
  statementIndex: number,
): string {
  const prefillData = { gradingId, statementIndex };
  const params = new URLSearchParams({
    attachmentId,
    prefill: JSON.stringify(prefillData),
  });
  return `${appPaths.settingsKnowledgeBase.build({ knowledgeBaseId })}?${params.toString()}`;
}

function groupSourcesByArticle(
  sources: readonly KnowledgeBaseAdherenceSource[],
): SourceGroup[] {
  const groups: SourceGroup[] = [];

  for (const source of sources) {
    const existing = groups.find(
      (g) =>
        g.knowledgeBaseId === source.knowledge_base_id &&
        g.attachmentId === source.attachment_id,
    );

    if (existing) {
      existing.quotes.push(source.quote);
    } else {
      groups.push({
        knowledgeBaseId: source.knowledge_base_id,
        attachmentId: source.attachment_id,
        quotes: [source.quote],
      });
    }
  }

  return groups;
}
